import type { AgendaAttributeValue, AgendaRecordItem, FormItemDefinition, RecordAttribute, RecordAttributeRes } from 'types';
import { getPortalSafeValue, initialAttributeValues, isPortalRecordsDefinition } from '../../agendaRecord/agendaRecord.ts';

export type AttributeInput = { formItemId: string } & Partial<RecordAttribute>;

const remap = {
  numberAttributeInput: 'number',
  computedAttributeInput: 'value',
  textAttributeInput: 'value',
  checkboxAttributeInput: 'value',
  phoneNumberAttributeInput: 'value',
  emailAttributeInput: 'value',
  radioButtonAttributeInput: 'value',
  dateAttributeInput: 'date',
  commentAttributeInput: null,
  fileUploadAttributeInput: 'values',
  richTextAttributeInput: ['plainText', 'richText'],
  recordsAttributeInput: 'records',
  tablesAttributeInput: 'rows',
  usersAttributeInput: 'users',
} as never;

/* eslint-disable @typescript-eslint/no-explicit-any */
export const normalizeAttributes = (attributes: any[]) => {
  return attributes.map((attr) => {
    const newAttr = { ...attr };

    for (const key in attr) {
      if (!remap[key]) continue;

      const newKey = remap[key];

      if (Array.isArray(newKey)) {
        if (attr[key]?.value) {
          newAttr[key] = {
            plainText: attr[key].value,
            richText: attr[key].value,
          };
        }
      } else if (newKey && attr[key]?.value !== undefined) {
        newAttr[key] = {
          [newKey]: attr[key].value,
        };
      }
    }

    return newAttr;
  });
};

export const convertAttributeToInput = (attributeData: RecordAttributeRes): AttributeInput => {
  const base: Record<string, unknown> = { ...initialAttributeValues };

  const formItemId = attributeData.formItem?.id ?? '';

  for (const key of Object.keys(base)) {
    const sourceKey = (key.includes('button') ? key : key.replace('Input', '')) as keyof RecordAttributeRes;

    if (attributeData[sourceKey] != null) {
      base[key] = attributeData[sourceKey];
    }
  }

  if (isPortalRecordsDefinition(attributeData.formItem?.definition)) {
    base.recordsAttributeInput = { records: [] };
  }

  return {
    formItemId,
    ...base,
  };
};

export const processFormAttributes = (items?: AgendaRecordItem[], buttonFormItemId?: string, rowId?: string, onlyActionInput?: boolean) => {
  const rowAttributes = items?.find((item) => item.id === rowId)?.attributes ?? [];

  const filteredAttributes = onlyActionInput ? rowAttributes.filter((attr) => attr.formItem?.isActionInput) : rowAttributes;

  const extendedAttributes = [
    ...filteredAttributes,
    {
      formItem: { id: buttonFormItemId },
      buttonAttributeInput: { isClicked: true },
    },
  ] as RecordAttributeRes[];

  return normalizeAttributes(extendedAttributes.map((item) => convertAttributeToInput(item)));
};

const KIND_TO_INPUT_KEY: Record<string, keyof typeof initialAttributeValues> = {
  Number: 'numberAttributeInput',
  Text: 'textAttributeInput',
  RichText: 'richTextAttributeInput',
  Date: 'dateAttributeInput',
  Checkbox: 'checkboxAttributeInput',
  Email: 'emailAttributeInput',
  Phone: 'phoneNumberAttributeInput',
  Radio: 'radioButtonAttributeInput',
  Users: 'usersAttributeInput',
  Comment: 'commentAttributeInput',
  File: 'fileUploadAttributeInput',
  Records: 'recordsAttributeInput',
  Tables: 'tablesAttributeInput',
  AutomaticSequence: 'automaticSequenceAttributeInput',
  Computed: 'computedAttributeInput',
  Button: 'buttonAttributeInput',
};

export function buildAttributePayload(formItemId?: string, kind?: string, value?: unknown, definition?: FormItemDefinition) {
  if (!formItemId || !kind || !value) return null;

  const key = Object.keys(KIND_TO_INPUT_KEY).find((k) => k.toLowerCase() === kind.toLowerCase())
    ? KIND_TO_INPUT_KEY[Object.keys(KIND_TO_INPUT_KEY).find((k) => k.toLowerCase() === kind.toLowerCase()) as string]
    : (kind as keyof typeof initialAttributeValues);

  if (!key || !(key in initialAttributeValues)) {
    throw new Error(`Unknown kind "${kind}". Please extend KIND_TO_INPUT_KEY.`);
  }

  const payload: any = {
    formItemId,
    ...initialAttributeValues,
  };

  const portalSafeValue = definition ? getPortalSafeValue(definition, value as AgendaAttributeValue) : value;
  const val = kind === 'Number' ? Number(portalSafeValue) : portalSafeValue;
  payload[key] = { value: val };

  return normalizeAttributes([payload])[0];
}
